/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AccessControlModal.tsx
 🗓️ Created: 2025-12-06
 🧩 Description:
    Modal component for creating/editing access control entries
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, ReactNode } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import Modal from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { createAccessControlEntry, updateAccessControlEntry, applyAccessControlRules } from '../api/accessControlApi';
import type { AccessControlEntry, AccessControlFormData } from '../types';

type FormFieldProps = {
  id: string;
  label: string;
  tooltip?: string;
  children: ReactNode;
  required?: boolean;
};

const FormField = ({ id, label, tooltip, children, required }: FormFieldProps) => (
  <div className="flex items-center gap-4">
    <div className="w-36 flex-shrink-0 h-10 flex items-center">
      {tooltip ? (
        <SimpleTooltip content={tooltip} position="top">
          <Label htmlFor={id} className="text-sm font-medium text-foreground cursor-help flex items-center gap-1">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
        </SimpleTooltip>
      ) : (
        <Label htmlFor={id} className="text-sm font-medium text-foreground flex items-center gap-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
      )}
    </div>
    <div className="flex-1">{children}</div>
  </div>
);

interface AccessControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  entry?: AccessControlEntry | null;
}

export function AccessControlModal({
  isOpen,
  onClose,
  onSuccess,
  entry,
}: AccessControlModalProps) {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AccessControlFormData>({
    name: '',
    description: '',
    ipAddress: '',
    listType: 'whitelist',
    protocol: 'all',
    sourcePort: '',
    destinationPort: '',
    interfaceName: '',
    direction: 'input',
    priority: 50,
    enabled: true,
  });

  useEffect(() => {
    if (entry) {
      const src = String(entry.source || '');
      const rawListType = String(entry.listType || (entry as any).list_type || (src === 'fail2ban' || src === 'pike' || src === 'irsf' || src === 'voipbl' ? 'blacklist' : 'whitelist')).toLowerCase();
      const listType = rawListType === 'blacklist' ? 'blacklist' : 'whitelist';

      const rawProtocol = String(entry.protocol || (entry as any).protocol || 'all').toLowerCase();
      const rawDirection = String(entry.direction || (entry as any).direction || 'input').toLowerCase();

      setFormData({
        name: entry.name || (entry as any).reason || '',
        description: entry.description || '',
        ipAddress: entry.ipAddress || (entry as any).ip_address || '',
        listType,
        protocol: (['all', 'tcp', 'udp', 'tcp/udp', 'icmp', 'icmpv6'].includes(rawProtocol) ? rawProtocol : 'all') as any,
        sourcePort: entry.sourcePort || '',
        destinationPort: entry.destinationPort || '',
        interfaceName: entry.interfaceName || '',
        direction: (['input', 'output', 'forward'].includes(rawDirection) ? rawDirection : 'input') as any,
        priority: entry.priority ?? 50,
        enabled: entry.enabled ?? true,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        ipAddress: '',
        listType: 'whitelist',
        protocol: 'all',
        sourcePort: '',
        destinationPort: '',
        interfaceName: '',
        direction: 'input',
        priority: 50,
        enabled: true,
      });
    }
  }, [entry, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      showError(t('accessControl.messages.nameRequired'));
      return;
    }

    if (!formData.ipAddress.trim()) {
      showError(t('accessControl.messages.ipAddressRequired'));
      return;
    }

    // Basic IP validation
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}(\/\d{1,3})?$/;
    if (!ipRegex.test(formData.ipAddress) && !ipv6Regex.test(formData.ipAddress)) {
      showError(t('accessControl.messages.invalidIpAddress'));
      return;
    }

    setLoading(true);

    try {
      const payload: AccessControlFormData = {
        ...formData,
        description: formData.description || null,
        sourcePort: formData.sourcePort || null,
        destinationPort: formData.destinationPort || null,
        interfaceName: formData.interfaceName || null,
      };

      if (entry) {
        await updateAccessControlEntry(entry.id, payload);
        showSuccess(t('accessControl.messages.updateSuccess'));
      } else {
        await createAccessControlEntry(payload);
        showSuccess(t('accessControl.messages.createSuccess'));
      }

      // Apply changes to the system firewall
      try {
        await applyAccessControlRules();
        showSuccess(t('accessControl.messages.appliedToFirewall'));
      } catch (applyError) {
        // Show warning but don't fail the operation
        showError(t('accessControl.messages.applyError') + ': ' + (applyError instanceof Error ? applyError.message : ''));
      }

      onSuccess();
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('accessControl.messages.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={entry ? t('accessControl.editEntry') : t('accessControl.addEntry')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          id="name"
          label={t('accessControl.form.name')}
          tooltip={t('accessControl.form.nameTooltip')}
          required
        >
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('accessControl.form.namePlaceholder')}
            required
            autoFocus
          />
        </FormField>

        <FormField
          id="description"
          label={t('accessControl.form.description')}
          tooltip={t('accessControl.form.descriptionTooltip')}
        >
          <Input
            id="description"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('accessControl.form.descriptionPlaceholder')}
          />
        </FormField>

        <FormField
          id="listType"
          label={t('accessControl.form.listType')}
          tooltip={t('accessControl.form.listTypeTooltip')}
          required
        >
          <Select
            value={formData.listType}
            onValueChange={(value: 'whitelist' | 'blacklist') =>
              setFormData({ ...formData, listType: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="whitelist">{t('accessControl.whitelist')}</SelectItem>
              <SelectItem value="blacklist">{t('accessControl.blacklist')}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          id="ipAddress"
          label={t('accessControl.form.ipAddress')}
          tooltip={t('accessControl.form.ipAddressTooltip')}
          required
        >
          <Input
            id="ipAddress"
            value={formData.ipAddress}
            onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
            placeholder="192.168.1.1 or 10.0.0.0/24"
            required
          />
        </FormField>

        <FormField
          id="protocol"
          label={t('accessControl.form.protocol')}
          tooltip={t('accessControl.form.protocolTooltip')}
        >
          <Select
            value={formData.protocol || 'all'}
            onValueChange={(value: AccessControlFormData['protocol']) =>
              setFormData({ ...formData, protocol: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('accessControl.protocol.all')}</SelectItem>
              <SelectItem value="tcp">TCP</SelectItem>
              <SelectItem value="udp">UDP</SelectItem>
              <SelectItem value="tcp/udp">TCP/UDP</SelectItem>
              <SelectItem value="icmp">ICMP</SelectItem>
              <SelectItem value="icmpv6">ICMPv6</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          id="direction"
          label={t('accessControl.form.direction')}
          tooltip={t('accessControl.form.directionTooltip')}
        >
          <Select
            value={formData.direction || 'input'}
            onValueChange={(value: AccessControlFormData['direction']) =>
              setFormData({ ...formData, direction: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="input">{t('accessControl.direction.input')}</SelectItem>
              <SelectItem value="output">{t('accessControl.direction.output')}</SelectItem>
              <SelectItem value="forward">{t('accessControl.direction.forward')}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          id="priority"
          label={t('accessControl.form.priority')}
          tooltip={t('accessControl.form.priorityTooltip')}
        >
          <Input
            id="priority"
            type="number"
            min="0"
            max="9999"
            value={formData.priority ?? 50}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 50 })}
          />
        </FormField>

        <FormField
          id="sourcePort"
          label={t('accessControl.form.sourcePort')}
          tooltip={t('accessControl.form.sourcePortTooltip')}
        >
          <Input
            id="sourcePort"
            value={formData.sourcePort || ''}
            onChange={(e) => setFormData({ ...formData, sourcePort: e.target.value })}
            placeholder="80 or 8000-8010"
          />
        </FormField>

        <FormField
          id="destinationPort"
          label={t('accessControl.form.destinationPort')}
          tooltip={t('accessControl.form.destinationPortTooltip')}
        >
          <Input
            id="destinationPort"
            value={formData.destinationPort || ''}
            onChange={(e) => setFormData({ ...formData, destinationPort: e.target.value })}
            placeholder="80 or 8000-8010"
          />
        </FormField>

        <FormField
          id="interfaceName"
          label={t('accessControl.form.interfaceName')}
          tooltip={t('accessControl.form.interfaceNameTooltip')}
        >
          <Input
            id="interfaceName"
            value={formData.interfaceName || ''}
            onChange={(e) => setFormData({ ...formData, interfaceName: e.target.value })}
            placeholder="eth0, ens33, etc."
          />
        </FormField>

        <FormField
          id="enabled"
          label={t('accessControl.form.enabled')}
          tooltip={t('accessControl.form.enabledTooltip')}
        >
          <Toggle
            value={formData.enabled ?? true}
            onChange={(enabled) => setFormData({ ...formData, enabled })}
          />
        </FormField>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
